import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { services } from "@/i18n/translations";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = ({ defaultService }) => {
  const { t, lang } = useLanguage();
  const routeLocation = useLocation();
  const prefill = routeLocation.state?.etaPrefill || {};
  const inboundLocation = routeLocation.state?.location;
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    yacht_name: prefill.yacht || "",
    location: prefill.location || inboundLocation || "",
    service: defaultService || "",
    message: prefill.message || "",
  });

  useEffect(() => {
    if (prefill.yacht || prefill.message || prefill.location) {
      setForm((prev) => ({
        ...prev,
        yacht_name: prefill.yacht || prev.yacht_name,
        location: prefill.location || prev.location,
        message: prefill.message || prev.message,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        language: lang,
      });
      trackEvent("form_submit", {
        form_id: "contact",
        service: form.service || "(none)",
        location: form.location || "(none)",
        language: lang,
      });
      toast.success(t.form.success);
      setForm({
        name: "",
        email: "",
        phone: "",
        yacht_name: "",
        location: "",
        service: defaultService || "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldCls =
    "w-full bg-transparent border-b border-navy/20 text-navy placeholder:text-navy/40 py-3 px-0 text-sm focus:outline-none focus:border-brand transition-colors";

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-6"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="overline block mb-2" htmlFor="cf-name">
            {t.form.name} <span className="text-brand">*</span>
          </label>
          <input
            id="cf-name"
            required
            name="name"
            value={form.name}
            onChange={onChange}
            className={fieldCls}
            data-testid="cf-name-input"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="cf-email">
            {t.form.email} <span className="text-brand">*</span>
          </label>
          <input
            id="cf-email"
            required
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            className={fieldCls}
            data-testid="cf-email-input"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="cf-phone">
            {t.form.phone}
          </label>
          <input
            id="cf-phone"
            name="phone"
            value={form.phone}
            onChange={onChange}
            className={fieldCls}
            data-testid="cf-phone-input"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="cf-yacht">
            {t.form.yacht}
          </label>
          <input
            id="cf-yacht"
            name="yacht_name"
            value={form.yacht_name}
            onChange={onChange}
            className={fieldCls}
            data-testid="cf-yacht-input"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="cf-location">
            {t.form.location}
          </label>
          <input
            id="cf-location"
            name="location"
            value={form.location}
            onChange={onChange}
            className={fieldCls}
            data-testid="cf-location-input"
          />
        </div>
        <div>
          <label className="overline block mb-2" htmlFor="cf-service">
            {t.form.service}
          </label>
          <select
            id="cf-service"
            name="service"
            value={form.service}
            onChange={onChange}
            className={`${fieldCls} appearance-none cursor-pointer`}
            data-testid="cf-service-select"
          >
            <option value="" className="bg-bone">
              {t.form.servicePlaceholder}
            </option>
            {services.map((s) => (
              <option key={s.slug} value={s[lang].name} className="bg-bone">
                {s[lang].name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="overline block mb-2" htmlFor="cf-message">
          {t.form.message} <span className="text-brand">*</span>
        </label>
        <textarea
          id="cf-message"
          required
          name="message"
          rows={5}
          placeholder={t.form.messagePlaceholder}
          value={form.message}
          onChange={onChange}
          className={`${fieldCls} resize-none`}
          data-testid="cf-message-input"
        />
      </div>

      <div className="pt-4">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-3 bg-brand text-white px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-brand-deep transition-colors disabled:opacity-60"
          data-testid="cf-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              {t.form.submitting}
            </>
          ) : (
            <>{t.form.submit} →</>
          )}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
